import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../../context/ConfigContext';
import { formatDueDayOptions } from '../../utils/formatters';

function AdminHelp() {
  const navigate = useNavigate();
  const { getPaymentDueDayOptions } = useConfig();
  const [selectedGuide, setSelectedGuide] = useState(null);
  const printRef = useRef();

  const quickStartSteps = [
    { step: 1, title: 'Set Up Branding', description: 'Upload your logo and set colors', link: '/admin/settings/branding' },
    { step: 2, title: 'Configure Business Settings', description: 'Set fees, financing tiers, and payment options', link: '/admin/settings/business' },
    { step: 3, title: 'Add Your States', description: 'Configure the states you operate in', link: '/admin/states' },
    { step: 4, title: 'Add Properties', description: 'List your first property for sale', link: '/admin/properties' },
    { step: 5, title: 'Create a Loan', description: 'Add customers and create loans', link: '/admin/customers-loans-hub' }
  ];

  const documentationGuides = [
    {
      title: 'Getting Started Guide',
      description: 'Complete setup walkthrough for new users',
      icon: '🚀',
      sections: [
        {
          heading: '1. Set Up Your Branding',
          content: [
            'Navigate to Settings > Branding from the admin dashboard',
            'Upload your company logo (recommended size: 200x60 pixels)',
            'Choose your primary and secondary brand colors',
            'Enter your company name and legal business name',
            'Add your contact information (email, phone, address)'
          ]
        },
        {
          heading: '2. Configure Business Settings',
          content: [
            'Go to Settings > Business to configure your terms',
            'Set your document fee (one-time fee charged at purchase)',
            'Configure late payment fees and grace periods',
            'Set up financing tiers with interest rates and terms',
            'Enable/disable features like tax escrow and HOA collection'
          ]
        },
        {
          heading: '3. Add Your Operating States',
          content: [
            'Navigate to States from the sidebar',
            'Click "Add State" for each state you operate in',
            'Add counties within each state',
            'Set state-specific settings if needed'
          ]
        },
        {
          heading: '4. Set Up Payment Processing',
          content: [
            'Go to Settings > Square Integration',
            'Connect your Square account for payment processing',
            'Test the connection with a small transaction',
            'Configure convenience fee settings'
          ]
        }
      ]
    },
    {
      title: 'Property Management Guide',
      description: 'How to list and manage properties',
      icon: '🏠',
      sections: [
        {
          heading: 'Adding New Properties',
          content: [
            'Go to Properties and click "Add Property"',
            'Enter the property title and description',
            'Select the state and county from your configured locations',
            'Enter acreage, APN (parcel number), and location details',
            'Upload photos (first photo becomes the featured image)',
            'Set the cash price and down payment options'
          ]
        },
        {
          heading: 'Setting Financing Options',
          content: [
            'Choose which financing tiers apply to this property',
            'The system automatically calculates monthly payments',
            'You can override calculated payments if needed',
            'Set minimum down payment requirements'
          ]
        },
        {
          heading: 'Property Status Management',
          content: [
            'Available: Property is listed for sale',
            'Coming Soon: Property shows on site but not purchasable',
            'Under Contract: Someone has started a purchase',
            'Sold: Property has been sold (loan is active)',
            'Change status from the property detail page'
          ]
        },
        {
          heading: 'Property Details & Features',
          content: [
            'Add land characteristics (terrain, vegetation, views)',
            'Specify available utilities (water, electric, septic)',
            'Note access type (paved road, dirt road, etc.)',
            'Include zoning information and restrictions',
            'Add GPS coordinates for mapping'
          ]
        }
      ]
    },
    {
      title: 'Loan Management Guide',
      description: 'Managing customer loans and payments',
      icon: '💰',
      sections: [
        {
          heading: 'Creating a New Loan',
          content: [
            'When a customer purchases a property, create a loan',
            'Go to Customers & Loans Hub and click "New Loan"',
            'Select or create the customer',
            'Choose the property and financing terms',
            `Set the payment due date (${formatDueDayOptions(getPaymentDueDayOptions())} of month)`,
            'The system generates the amortization schedule'
          ]
        },
        {
          heading: 'Recording Payments',
          content: [
            'Go to the loan detail page',
            'Click "Record Payment" to add a payment',
            'Enter the payment amount and date',
            'Select payment method (Square, check, cash, etc.)',
            'The system updates the loan balance automatically'
          ]
        },
        {
          heading: 'Handling Late Payments',
          content: [
            'Late fees are applied after the grace period',
            'View overdue loans from the dashboard',
            'Send payment reminders to customers',
            'Generate cure letters for seriously delinquent accounts',
            'Track payment arrangements and promises'
          ]
        },
        {
          heading: 'Loan Modifications',
          content: [
            'Generate payoff quotes for early payoff',
            'Process partial payoffs',
            'Modify loan terms if needed (with new contract)',
            'Handle loan assumptions (transfer to new buyer)'
          ]
        }
      ]
    },
    {
      title: 'Customer Portal Guide',
      description: 'What your customers can do',
      icon: '👥',
      sections: [
        {
          heading: 'Customer Dashboard',
          content: [
            'Customers log in to see their active loans',
            'Dashboard shows loan balance, next payment due',
            'Quick access to make a payment',
            'View property details and contract status'
          ]
        },
        {
          heading: 'Contracts & Documents',
          content: [
            'View Contract for Deed directly in the portal',
            'Electronic signature for contract signing',
            'Download signed contract as PDF',
            'Print contract for personal records',
            'Contract status tracking (pending, awaiting signature, fully executed)'
          ]
        },
        {
          heading: 'Online Payments',
          content: [
            'Customers can pay via credit/debit card through Square',
            'Convenience fee is clearly displayed before payment',
            'Payment confirmation sent via email',
            'Payment immediately reflects in their account'
          ]
        },
        {
          heading: 'Payment History',
          content: [
            'Full history of all payments made',
            'Downloadable payment receipts',
            'Year-end tax summaries (interest paid)',
            'Amortization schedule showing payoff progress'
          ]
        },
        {
          heading: 'Account Management',
          content: [
            'Update contact information',
            'Change email and password',
            'Access property documents and photos'
          ]
        }
      ]
    },
    {
      title: 'Reports & Analytics Guide',
      description: 'Understanding your business metrics',
      icon: '📊',
      sections: [
        {
          heading: 'Dashboard Overview',
          content: [
            'Total portfolio value and active loans',
            'Payments received this month/year',
            'Overdue accounts requiring attention',
            'Recent activity feed'
          ]
        },
        {
          heading: 'Financial Reports',
          content: [
            'Payment reports by date range',
            'Receivables aging report',
            'Income projections based on active loans',
            'Export data to CSV for accounting software'
          ]
        },
        {
          heading: 'Tax Reports',
          content: [
            'Generate 1098 forms for customer interest paid',
            'Interest income summary for your taxes',
            'Property tax payment tracking',
            'Year-end reports for your CPA'
          ]
        },
        {
          heading: 'Customer Reports',
          content: [
            'Customer account statements',
            'Payment history by customer',
            'Contact information exports',
            'Loan status summaries'
          ]
        }
      ]
    }
  ];

  const supportOptions = [
    {
      title: 'Contact Support',
      description: 'Get help from our team',
      icon: '📧',
      action: () => window.open('https://deedstack.com/contact', '_blank')
    },
    {
      title: 'View Pricing',
      description: 'Upgrade your plan or view features',
      icon: '💎',
      action: () => window.open('https://deedstack.com/pricing', '_blank')
    }
  ];

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${selectedGuide.title} - DeedStack</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #2d5a27; border-bottom: 2px solid #2d5a27; padding-bottom: 10px; }
            h2 { color: #333; margin-top: 30px; }
            ul { line-height: 1.8; }
            li { margin-bottom: 8px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 0.9em; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <div class="footer">
            <p>Generated from DeedStack Help Documentation</p>
            <p>For more help, visit deedstack.com/contact</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = () => {
    // For simplicity, we'll use the print dialog which allows saving as PDF
    handlePrint();
  };

  return (
    <div className="admin-dashboard-container">
      {/* Header with Back Button */}
      <div className="admin-hub-header">
        <button
          onClick={() => navigate('/admin')}
          className="admin-hub-back-btn"
        >
          <span className="admin-hub-back-arrow">←</span>
          Back to Dashboard
        </button>
        <h1 className="admin-page-title">Help & Documentation</h1>
        <p className="admin-hub-subtitle">Guides, tutorials, and support resources</p>
      </div>

      {/* Quick Start Guide */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2 style={{ marginBottom: '15px', color: 'var(--color-brand-text)' }}>
          🚀 Quick Start Guide
        </h2>
        <p style={{ color: 'var(--color-text-light)', marginBottom: '20px' }}>
          Follow these steps to get your land financing platform up and running.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {quickStartSteps.map((item) => (
            <div
              key={item.step}
              onClick={() => navigate(item.link)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '12px 15px',
                backgroundColor: 'var(--color-background-alt)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary-light)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'var(--color-background-alt)'}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                {item.step}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>{item.title}</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--color-text-light)' }}>{item.description}</div>
              </div>
              <span style={{ color: 'var(--color-brand-text)' }}>→</span>
            </div>
          ))}
        </div>
      </div>

      {/* Documentation Guides */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2 style={{ marginBottom: '15px', color: 'var(--color-brand-text)' }}>
          📚 Documentation Guides
        </h2>
        <p style={{ color: 'var(--color-text-light)', marginBottom: '20px' }}>
          Click on any guide to view detailed instructions, print, or save as PDF.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
          {documentationGuides.map((guide, index) => (
            <button
              key={index}
              onClick={() => setSelectedGuide(guide)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '20px',
                backgroundColor: 'var(--color-background-alt)',
                border: '2px solid transparent',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'border-color 0.2s, transform 0.2s',
                textAlign: 'left'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ fontSize: '2rem' }}>{guide.icon}</div>
              <div>
                <div style={{ fontWeight: '600', color: 'var(--color-text)', marginBottom: '4px' }}>
                  {guide.title}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)' }}>
                  {guide.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Support Options */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2 style={{ marginBottom: '15px', color: 'var(--color-brand-text)' }}>
          💬 Get Support
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
          {supportOptions.map((option, index) => (
            <button
              key={index}
              onClick={option.action}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '25px 20px',
                backgroundColor: 'var(--color-background-alt)',
                border: '2px solid transparent',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'border-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--color-primary)'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = 'transparent'}
            >
              <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>{option.icon}</div>
              <div style={{ fontWeight: '600', color: 'var(--color-text)', marginBottom: '5px' }}>
                {option.title}
              </div>
              <div style={{ fontSize: '0.9rem', color: 'var(--color-text-light)', textAlign: 'center' }}>
                {option.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Keyboard Shortcuts */}
      <div className="card">
        <h2 style={{ marginBottom: '15px', color: 'var(--color-brand-text)' }}>
          ⌨️ Keyboard Shortcuts
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: 'var(--color-background-alt)', borderRadius: '5px' }}>
            <span>Submit form</span>
            <kbd style={{ backgroundColor: 'var(--color-surface-sunken)', padding: '2px 8px', borderRadius: '3px', fontSize: '0.85rem' }}>Enter</kbd>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: 'var(--color-background-alt)', borderRadius: '5px' }}>
            <span>Close modal</span>
            <kbd style={{ backgroundColor: 'var(--color-surface-sunken)', padding: '2px 8px', borderRadius: '3px', fontSize: '0.85rem' }}>Esc</kbd>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: 'var(--color-background-alt)', borderRadius: '5px' }}>
            <span>Navigate fields</span>
            <kbd style={{ backgroundColor: 'var(--color-surface-sunken)', padding: '2px 8px', borderRadius: '3px', fontSize: '0.85rem' }}>Tab</kbd>
          </div>
        </div>
      </div>

      {/* Documentation Modal */}
      {selectedGuide && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setSelectedGuide(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 25px',
              borderBottom: '1px solid var(--color-border)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '1.5rem' }}>{selectedGuide.icon}</span>
                <h2 style={{ margin: 0, color: 'var(--color-brand-text)' }}>{selectedGuide.title}</h2>
              </div>
              <button
                onClick={() => setSelectedGuide(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--color-text-light)',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div
              ref={printRef}
              style={{
                padding: '25px',
                overflowY: 'auto',
                flex: 1
              }}
            >
              <h1 style={{ display: 'none' }}>{selectedGuide.title}</h1>
              {selectedGuide.sections.map((section, idx) => (
                <div key={idx} style={{ marginBottom: '25px' }}>
                  <h3 style={{
                    color: 'var(--color-brand-text)',
                    marginBottom: '12px',
                    fontSize: '1.1rem'
                  }}>
                    {section.heading}
                  </h3>
                  <ul style={{
                    margin: 0,
                    paddingLeft: '20px',
                    lineHeight: '1.7'
                  }}>
                    {section.content.map((item, i) => (
                      <li key={i} style={{ marginBottom: '8px', color: 'var(--color-text)' }}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              padding: '15px 25px',
              borderTop: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface-sunken)'
            }}>
              <button
                onClick={handleDownloadPDF}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                📄 Download PDF
              </button>
              <button
                onClick={handlePrint}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                🖨️ Print
              </button>
              <button
                onClick={() => setSelectedGuide(null)}
                className="btn btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminHelp;
